import { useTranslation } from "@shared/utils/internationalization";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";
import Input from "@ui-kit/form/Input/Input.web";
import Textarea from "@ui-kit/form/Textarea/Textarea.web";
import Checkbox from "@ui-kit/form/Checkbox/Checkbox.web";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Text from "@ui-kit/typography/Text/Text.web";
import useImageInsertModal from "./useImageInsertModal";
import { ImageInsertModalProps } from "./ImageInsertModal.props";

export default function ImageInsertModal({
  isOpen,
  onClose,
  onInsert,
}: ImageInsertModalProps) {
  const { t } = useTranslation("common");
  const {
    src,
    setSrc,
    alt,
    setAlt,
    width,
    height,
    constrainProportions,
    setConstrainProportions,
    isUploading,
    fileInputRef,
    handleFileSelect,
    handleFileChange,
    handleWidthChange,
    handleHeightChange,
    handleSubmit,
    handleCancel,
  } = useImageInsertModal();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => handleCancel(onClose)}
      size="lg"
      isCentered
    >
      <Modal.Header>
        <Text fontSize="lg" fontWeight="600">
          {t("Insert/edit image")}
        </Text>
      </Modal.Header>

      <Modal.Body>
        <VStack spacing="20px" align="stretch">
          <FormControl>
            <FormLabel htmlFor="image-source">
              <Text fontSize="sm" fontWeight="500">
                Source
              </Text>
            </FormLabel>
            <HStack spacing="8px">
              <Input
                id="image-source"
                value={src}
                onChange={(e) => setSrc(e.target.value)}
                placeholder={t("Enter image URL")}
                flex="1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleFileSelect}
                leftIcon={<Icon.Image.Outline boxSize="element.md" />}
                px="sm"
                isLoading={isUploading}
                isDisabled={isUploading}
                data-testid="browse-image-button"
              >
                {t("Browse")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
                hidden
              />
            </HStack>
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="image-description">
              <Text fontSize="sm" fontWeight="500">
                Image description
              </Text>
            </FormLabel>
            <Textarea
              id="image-description"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder={t("Enter image description")}
              resize="none"
              rows={3}
            />
          </FormControl>

          <FormControl>
            <FormLabel>
              <Text fontSize="sm" fontWeight="500">
                {t("Dimensions")}
              </Text>
            </FormLabel>
            <HStack spacing="12px" align="center">
              <Input
                type="number"
                value={width || ""}
                onChange={(e) => handleWidthChange(Number(e.target.value) || 0)}
                placeholder={t("Width")}
                width="element.6xl"
              />
              <Icon.X.Outline />
              <Input
                type="number"
                value={height || ""}
                onChange={(e) =>
                  handleHeightChange(Number(e.target.value) || 0)
                }
                placeholder={t("Height")}
                width="element.6xl"
              />
              <Checkbox
                isChecked={constrainProportions}
                onChange={(e) => setConstrainProportions(e.target.checked)}
              >
                <Text fontSize="sm">{t("Constrain proportions")}</Text>
              </Checkbox>
            </HStack>
          </FormControl>
        </VStack>
      </Modal.Body>

      <Modal.Footer>
        <Button
          colorPalette="primary"
          variant="solid"
          onClick={() => handleSubmit(onInsert, onClose)}
          isDisabled={!src.trim()}
          data-testid="insert-image-ok-button"
        >
          {t("Ok")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => handleCancel(onClose)}
          data-testid="insert-image-cancel-button"
        >
          {t("Cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
